/**
 * 主题偏好的**本地防闪缓存** —— `AC-N3.3` 的反面纪律：**它不是唯一载体**
 *
 * ## 🔴 为什么需要它（以及它为什么不能是"唯一载体"）
 *
 * 服务端是权威（`AC-N3.2`：换设备保持）—— 但服务端值要**一次异步请求**才拿得到。
 * 没有缓存的话，**每次刷新都先按「跟随系统」画一帧**，用户选的是深色却看到白屏一闪（FOUC）。
 * ⇒ 缓存唯一的职责是**首屏同步给出上一次的取值**，随后被服务端值覆盖。
 *
 * 🔴 `AC-N3.3` 明禁"以纯 `localStorage` 作为唯一载体" —— 本文件**只写不读权威**：
 * 登出**不清缓存**（清了就会闪），但它**永远不会覆盖**服务端下发的值（见 `app/store.tsx`
 * 的 `applyServerTheme`）。
 *
 * ## 🔴 为什么每一步都要 `?.` 与 `try`
 *
 * 端侧测试基座（`test/render.tsx`）只挂了**最小 `localStorage`**（仅 `getItem`），
 * 且浏览器在隐私模式下会**抛异常**而不是返回 `null`。
 * ⇒ 一律 `?.getItem?.()` / `?.setItem?.()` + `try` —— 存储不可用时**静默降级**，
 * 绝不能因为"存不了主题"把整个应用挂掉（那是把锦上添花的东西做成了单点故障）。
 */

import { ThemeValues, type ThemeValue } from '@jiaying/shared';

/** 存储键 —— 🔴 带版本号（取值集变化时旧值会被安全忽略，而不是被当成非法值崩掉） */
export const THEME_CACHE_KEY = 'jiaying.theme.v1';

/** 读缓存；无值 / 非法值 / 存储不可用 ⇒ `null`（由调用方回落 `'system'`） */
export function loadCachedTheme(): ThemeValue | null {
  try {
    const raw = globalThis.localStorage?.getItem?.(THEME_CACHE_KEY) ?? null;
    if (raw === null) return null;
    // 🔴 逐个比对取值集 —— 不 `as ThemeValue` 硬转（旧版本存的值可能已不在集合里）
    return (ThemeValues as readonly string[]).includes(raw) ? (raw as ThemeValue) : null;
  } catch {
    return null;
  }
}

/** 写缓存；存储不可用 ⇒ 静默忽略（服务端仍是权威） */
export function saveCachedTheme(pref: ThemeValue): void {
  try {
    globalThis.localStorage?.setItem?.(THEME_CACHE_KEY, pref);
  } catch {
    /* 隐私模式 / 配额满 / 无存储 ⇒ 本次会话闪一下而已，不影响正确性 */
  }
}
